import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Form, Container } from 'react-bootstrap';
import { useAuth } from "../AuthContext/AuthContext";
import './LoginPage.css';
import {useTranslation} from 'react-i18next';

const LoginPage = () => {
    const {t} = useTranslation('login')
    // Initialize hooks and state
    const navigate = useNavigate();
    const { login, logout, authError, isLoggedIn, loading } = useAuth();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showAlert, setShowAlert] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    

    // Redirect to dashboard if already logged in
    useEffect(() => {
        if (isLoggedIn) {
            console.log('User is logged in, navigating to /dashboard');
            navigate('/dashboard');
        }
    }, [isLoggedIn, navigate]);

    // Show alert when auth error occurs
    useEffect(() => {
        setShowAlert(!!authError);
    }, [authError]);

    // Handle form submission
    const handleLogin = async (event) => {
        event.preventDefault();

        // Clear any existing sessions
        console.log(t('loggingOutBeforeAttemptingNewLogin'));
        await logout();

        // Normalize the username
        const normalizedUsername = username.trim().toLowerCase();
        console.log(t('attemptingToLogInWithUsername'), normalizedUsername);

        try {
            await login(normalizedUsername, password);
            console.log(t('loginSuccessful'));

            // Store remember me preference if selected
            if (rememberMe) {
                localStorage.setItem('rememberMe', 'true');
                localStorage.setItem('username', normalizedUsername);
            } else {
                localStorage.removeItem('rememberMe');
                localStorage.removeItem('username');
            }
        } catch (error) {
            console.error(t('loginFailed'), error);
        }
    };

    return (
        <div className="login-background">
            <Container className="login-container">
                <Card className="login-card">
                    <Card.Body className="p-0">
                        <div className="login-grid">
                            {/* Branding Section */}
                            <div className="branding-section">
                                <h1 className="welcome-title">{t('welcomeback')}</h1>
                                <p className="welcome-text">{t('signaccessdashboard')}</p>
                                <div className="support-text">
                                    <p>{t('needhelpcontact')}</p>
                                </div>
                            </div>

                            {/* Form Section */}
                            <div className="form-section">
                                <div className="form-header">
                                    <h2>{t('logintoyouraccount')}</h2>
                                    <p>{t('enteryourcredentials')}</p>
                                </div>

                                {showAlert && (
                                    <Alert
                                        variant="danger"
                                        onClose={() => setShowAlert(false)}
                                        dismissible
                                        className="error-alert"
                                    >
                                        {authError}
                                    </Alert>
                                )}

                                <Form onSubmit={handleLogin} className="login-form">
                                    <Form.Group className="form-group">
                                        <Form.Label>{t('username')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                            placeholder={t('enteryourusername')}
                                            required
                                        />
                                    </Form.Group>

                                    <Form.Group className="form-group">
                                        <Form.Label>{t('password')}</Form.Label>
                                        <Form.Control
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder={t('enteryourpassword')}
                                            required
                                        />
                                    </Form.Group>

                                    <div className="form-options">
                                        <Form.Group className="mb-0">
                                            <Form.Check
                                                type="checkbox"
                                                label={t('rememberme')}
                                                checked={rememberMe}
                                                onChange={(e) => setRememberMe(e.target.checked)}
                                                className="remember-me"
                                            />
                                        </Form.Group>
                                        <Button
                                            variant="link"
                                            onClick={() => navigate('/reset-password')}
                                            className="reset-link"
                                        >
                                            {t('forgotpassword')}?
                                        </Button>
                                    </div>

                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={loading}
                                        className="submit-button"
                                    >
                                        {loading ? `${t('loggingin')}...` : t('login')}
                                    </Button>
                                </Form>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </Container>
        </div>
    );
};

export default LoginPage;